import isNil from 'lodash/isNil'
import type { ChangeEventHandler, KeyboardEventHandler, MouseEventHandler } from 'react'
import React, { useCallback, useContext as useContextCore, useMemo, useState } from 'react'
import { useContext as useContextParent } from '../Context'
import { useDebounce } from 'react-use'
import { useMongoSearch } from './useMongoSearch'
import type { ListField } from '@ztechapps/common.hook'
import { useDeepCompareEffect } from '@ztechapps/common.hook'
import isEmpty from 'lodash/isEmpty'
import find from 'lodash/find'

const defaultAllField = { name: 'all', displayName: 'All' }

export type SearchInputProviderProps = {
  children: React.ReactNode
}

export type SearchInputContextType = {
  search: (field: ListField) => void
  handleChange: ChangeEventHandler<HTMLInputElement>
  handleSubmit: MouseEventHandler<HTMLButtonElement>
  onKeyDown: KeyboardEventHandler<HTMLInputElement>
  displaySearchFields: ListField[]
} & Omit<SearchInputProviderProps, 'children'>

const context = React.createContext<SearchInputContextType>({} as SearchInputContextType)
const CoreProvider = context.Provider

export const SearchInputProvider = ({ children }) => {
  const {
    commonFields,
    searchValue,
    setSearchValue,
    searchField,
    setSearchField,
    onFilter,
    onChangeSearchField,
    searchType,
    debounceTime = 1500,
    searchOnChange = false,
  } = useContextParent()

  const [canDebounceSearch, setCanDebounceSearch] = useState(false)

  //these are the search fields specified — memoized so its `?? []` fallback doesn't hand
  //buildSearchFields (below) a new array identity on every render
  const commonSearchFields = useMemo(() => commonFields?.searchFields ?? [], [commonFields?.searchFields])
  const displaySearchFields: ListField[] = [defaultAllField, ...commonSearchFields]

  //this will build which fields to search, if all is specified, use commonSearchFields
  const buildSearchFields = useCallback(
    (field?: ListField) => {
      if (field?.name !== defaultAllField.name) {
        return [field]
      } else {
        return commonSearchFields
      }
    },
    [commonSearchFields]
  )

  const _mongoSearch = useMongoSearch({
    searchType,
    onFilter,
    searchFields: buildSearchFields(searchField),
    searchValue,
  })

  const search = (field: ListField = searchField) => {
    //set the active search fields
    onChangeSearchField(field)

    const searchFields = buildSearchFields(field)
    return _mongoSearch(null, null, searchFields)
  }

  useDeepCompareEffect(() => {
    if (isEmpty(displaySearchFields) || !setSearchField) return

    //check if there is already value on searchField,
    //if no value, set the the default value to all or check if the commonFields have defaultOnSearch  value to true
    if (!searchField) {
      const defaultSearchField =
        find(displaySearchFields, (item) => {
          return item?.defaultOnSearch
        }) ?? defaultAllField

      setSearchField(defaultSearchField)
    }
  }, [displaySearchFields])

  //everytime there is update on the search value, auto search after 1.5secs on default
  useDebounce(
    () => {
      if (searchOnChange && !isNil(searchValue) && canDebounceSearch) {
        search()
      }
    },
    debounceTime,
    [searchValue]
  )

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value
    setSearchValue(value)
    if (isEmpty(value)) {
      //to enable the clear of text-input and search we need to set this flag
      setCanDebounceSearch(true)
    }
  }

  const handleSubmit: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault()
    search()
  }

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      search()
      setCanDebounceSearch(false)
    } else {
      setCanDebounceSearch(true)
    }
  }

  return (
    <CoreProvider
      value={{
        handleChange,
        handleSubmit,
        onKeyDown,
        search,
        displaySearchFields,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useSearchInputContext = () => useContextCore(context)
